// App shell: sidebar (desktop) / bottom tabs (phone), top bar with streak, coins
// and account, the ⌘K command palette and the global daily-limit dialog.
import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { liveStreak, useLearn, useSyncStatus } from "../lib/state";
import { CommandPalette } from "./CommandPalette";
import { LimitDialog } from "./LimitDialog";
import { Pashmak } from "./Pashmak";
import { Fa } from "./Phrase";
import {
  IHome, IPhone, IChat, IHeadphones, IPath, ICards, IScript, IMic, IShop, ISettings, IFlame, ICoin, ISearch, ICrown, IMore,
} from "./icons";

export const NAV: { to: string; label: string; icon: (p: any) => ReactNode; key?: string; mobile?: boolean }[] = [
  { to: "/", label: "Home", icon: IHome, key: "h", mobile: true },
  { to: "/talk", label: "Talk", icon: IPhone, key: "t", mobile: true },
  { to: "/chat", label: "Chat", icon: IChat, key: "c", mobile: true },
  { to: "/learn", label: "Learn", icon: IPath, key: "l", mobile: true },
  { to: "/listen", label: "Listen", icon: IHeadphones, key: "s" },
  { to: "/words", label: "Words", icon: ICards, key: "w" },
  { to: "/pronounce", label: "Pronounce", icon: IMic, key: "p" },
  { to: "/script", label: "Alphabet", icon: IScript, key: "a" },
  { to: "/bazaar", label: "Bazaar", icon: IShop, key: "b" },
];

export function Layout() {
  const { tier } = useAuth();
  const [palette, setPalette] = useState(false);
  const [more, setMore] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const streak = useLearn((s) => liveStreak(s));
  const coins = useLearn((s) => s.coins);
  const due = useLearn((s) => Object.values(s.words).filter((w) => !w.deleted && w.due <= Date.now()).length);
  const sync = useSyncStatus();

  useEffect(() => setMore(false), [loc.pathname]);
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((p) => !p);
      }
      // g then a letter jumps to a section (like Gmail/GitHub).
      const t = e.target as HTMLElement;
      if (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable || document.querySelector("dialog[open]")) return;
      if (e.key === "g") {
        const next = (ev: KeyboardEvent) => {
          const item = NAV.find((n) => n.key === ev.key.toLowerCase());
          if (item) nav(item.to);
          window.removeEventListener("keydown", next, true);
        };
        window.addEventListener("keydown", next, true);
        setTimeout(() => window.removeEventListener("keydown", next, true), 1200);
      }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [nav]);

  // Full-screen experiences (the voice call) hide the chrome.
  const immersive = /^\/talk\/[^/]+/.test(loc.pathname) || /^\/learn\/[^/]+/.test(loc.pathname) || loc.pathname === "/words/review";

  if (immersive)
    return (
      <>
        <Outlet />
        <LimitDialog />
      </>
    );

  return (
    <div className="shell">
      <aside className="sidebar" aria-label="Main">
        <NavLink to="/" className="brand" aria-label="Pashmak home">
          <Pashmak size={34} plain />
          <span>Pashmak</span>
          <Fa>پشمک</Fa>
        </NavLink>
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} className={({ isActive }) => `navlink${isActive ? " active" : ""}`}>
            <n.icon />
            <span>{n.label}</span>
            {n.to === "/words" && due > 0 && <span className="chip firuze tiny" style={{ marginLeft: "auto", padding: "0 8px" }}>{due}</span>}
          </NavLink>
        ))}
        <div className="spacer" />
        {tier !== "pro" && (
          <NavLink to="/pro" className={({ isActive }) => `navlink${isActive ? " active" : ""}`} style={{ color: "var(--gold)" }}>
            <ICrown />
            <span>Get Pro</span>
          </NavLink>
        )}
        <NavLink to="/settings" className={({ isActive }) => `navlink${isActive ? " active" : ""}`}>
          <ISettings />
          <span>Settings</span>
        </NavLink>
        <button className="navlink" style={{ border: 0, background: "none", cursor: "pointer" }} onClick={() => setPalette(true)}>
          <ISearch />
          <span>Search</span>
          <kbd>⌘K</kbd>
        </button>
      </aside>

      <div className="main">
        <header className="topbar">
          <NavLink to="/" className="brand hide-desktop" style={{ padding: 0 }} aria-label="Pashmak home">
            <Pashmak size={30} plain />
            <span style={{ fontSize: 18 }}>Pashmak</span>
          </NavLink>
          <div className="grow" />
          {sync === "offline" && <span className="chip rose small" title="Changes are saved on this device and will sync when you're back online">Offline</span>}
          <span className="chip gold" title={`${streak}-day streak`} aria-label={`${streak} day streak`}>
            <IFlame width={16} height={16} /> {streak}
          </span>
          <NavLink to="/bazaar" className="chip" title="Sekke coins: spend them in the Bazaar" aria-label={`${coins} coins`} style={{ textDecoration: "none" }}>
            <ICoin width={16} height={16} /> {coins}
          </NavLink>
          {tier === "pro" && <span className="chip firuze" title="Pashmak Pro">Pro</span>}
          <button className="iconbtn hide-desktop" onClick={() => setPalette(true)} aria-label="Search">
            <ISearch />
          </button>
        </header>
        <Outlet />
      </div>

      <nav className="tabbar" aria-label="Main">
        {NAV.filter((n) => n.mobile).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} className={({ isActive }) => (isActive ? "active" : "")}>
            <n.icon />
            {n.label}
          </NavLink>
        ))}
        <button onClick={() => setMore((m) => !m)} aria-expanded={more} className={more ? "active" : ""}>
          <IMore />
          More
        </button>
      </nav>
      {more && (
        <div className="card hide-desktop" style={{ position: "fixed", right: 12, bottom: "calc(80px + env(safe-area-inset-bottom))", zIndex: 31, padding: 8, minWidth: 220 }}>
          {[...NAV.filter((n) => !n.mobile), ...(tier !== "pro" ? [{ to: "/pro", label: "Get Pro", icon: ICrown }] : []), { to: "/settings", label: "Settings", icon: ISettings }].map((n) => (
            <NavLink key={n.to} to={n.to} className="navlink">
              <n.icon />
              {n.label}
            </NavLink>
          ))}
        </div>
      )}
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
      <LimitDialog />
    </div>
  );
}
